import { useState } from "react";
import { toast } from "sonner";
import { X, Ticket, Check, Clock, Smartphone, Share2, Bell, RotateCcw, Play, Trophy, UserPlus, Zap } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import { shareNicaBros, ELTIMON_SHARE_URL, featuredShareUrl } from "@/lib/share";
import { shareRunCard } from "@/lib/giftcard";
import { ShareRunButton } from "@/components/GiftCardExtras";

const base = `${process.env.PUBLIC_URL || ""}/game-assets`;

// End-of-level partner reward: a 10% off El Timón coupon the player can save to
// their phone. Expires 7 days after it was issued (expires_at from the backend).
export function CouponModal({ coupon, level = null, onClose, onReplay, onPlayManagua, score = 0, user = null, onAuth, challenge = null, entry = null, run = null, onChallenge }) {
  const { lang, t } = useLang();
  const es = lang === "es";
  // Partner branding comes from the featured level config (El Timón is the default/template).
  const P = {
    name: level?.sponsorName || "El Timón",
    full: level?.sponsorFullName || level?.sponsorName || "Restaurante El Timón",
    city: level?.city || "San Juan del Sur",
    logo: level?.sponsorLogo || "eltimon_logo",
    key: level?.shareKey || "eltimon",
    emoji: level?.sponsorEmoji || "🍤",
    shareUrl: level ? featuredShareUrl(level) : ELTIMON_SHARE_URL,
  };
  const [saved, setSaved] = useState(false);
  const [email, setEmail] = useState("");
  const [leadSaving, setLeadSaving] = useState(false);
  const [leadDone, setLeadDone] = useState(() => !!localStorage.getItem("nicabros_score_email"));
  if (!coupon) return null;

  const expDate = coupon.expires_at ? new Date(coupon.expires_at) : null;
  const expLabel = expDate
    ? expDate.toLocaleDateString(es ? "es-NI" : "en-US", { day: "numeric", month: "short", year: "numeric" })
    : "";

  const captureEmail = async () => {
    const e = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error(t("scoreEmailInvalid")); return; }
    setLeadSaving(true);
    try {
      await api.captureLead({ email: e, source: `coupon_${P.key}` });
      localStorage.setItem("nicabros_score_email", e);
      setLeadDone(true);
      toast.success(t("scoreEmailThanks"));
    } catch (_) {
      toast.error(t("scoreEmailFail"));
    } finally {
      setLeadSaving(false);
    }
  };

  const loadImg = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });

  const buildCouponImage = async () => {
    const W = 1000, H = 1250;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const x = c.getContext("2d");
    const g = x.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#12294f"); g.addColorStop(1, "#0a1c38");
    x.fillStyle = g; x.fillRect(0, 0, W, H);
    x.strokeStyle = "#FFD54F"; x.lineWidth = 10;
    x.strokeRect(30, 30, W - 60, H - 60);
    x.textAlign = "center";

    const [nb, et] = await Promise.all([
      loadImg(`${base}/nicabros-logo.webp`),
      loadImg(`${base}/${P.logo}.webp`),
    ]);
    const ls = 190, ly = 90;
    if (nb) { x.fillStyle = "rgba(255,255,255,0.95)"; x.fillRect(230, ly, ls, ls); x.drawImage(nb, 240, ly + 10, ls - 20, ls - 20); }
    x.fillStyle = "#ffffff"; x.font = "bold 70px sans-serif"; x.fillText("×", W / 2, ly + ls / 2 + 24);
    if (et) { x.fillStyle = "rgba(255,255,255,0.95)"; x.fillRect(W - 230 - ls, ly, ls, ls); x.drawImage(et, W - 220 - ls, ly + 10, ls - 20, ls - 20); }

    x.fillStyle = "#FFD54F"; x.font = "bold 150px sans-serif";
    x.fillText(`${coupon.percent || 10}% OFF`, W / 2, ly + ls + 190);
    x.fillStyle = "#ffffff"; x.font = "46px sans-serif";
    x.fillText(es ? "de toda tu cuenta" : "your entire bill", W / 2, ly + ls + 260);
    x.font = "bold 52px sans-serif";
    x.fillText(P.full, W / 2, ly + ls + 330);
    x.font = "40px sans-serif"; x.fillStyle = "rgba(255,255,255,0.8)";
    x.fillText(`${P.city}, Nicaragua 🇳🇮`, W / 2, ly + ls + 390);

    x.fillStyle = "rgba(255,255,255,0.12)";
    const bx = 120, by = ly + ls + 450, bw = W - 240, bh = 150;
    x.fillRect(bx, by, bw, bh);
    x.strokeStyle = "rgba(255,255,255,0.3)"; x.lineWidth = 4; x.strokeRect(bx, by, bw, bh);
    x.fillStyle = "rgba(255,255,255,0.6)"; x.font = "34px sans-serif";
    x.fillText(es ? "TU CÓDIGO" : "YOUR CODE", W / 2, by + 55);
    x.fillStyle = "#ffffff"; x.font = "bold 64px monospace";
    x.fillText(coupon.code, W / 2, by + 120);

    if (expLabel) {
      x.fillStyle = "#FFD54F"; x.font = "40px sans-serif";
      x.fillText(`${es ? "Vence el" : "Expires"} ${expLabel}`, W / 2, by + bh + 90);
    }
    x.fillStyle = "rgba(255,255,255,0.5)"; x.font = "32px sans-serif";
    x.fillText("nicabros.com", W / 2, H - 70);

    return new Promise((resolve) => c.toBlob(resolve, "image/png"));
  };

  const saveToPhone = async () => {
    // Persist locally so it survives on the player's device.
    try {
      const list = JSON.parse(localStorage.getItem("nicabros_coupons") || "[]");
      if (!list.some((c) => c.code === coupon.code)) list.unshift(coupon);
      localStorage.setItem("nicabros_coupons", JSON.stringify(list.slice(0, 20)));
    } catch {}
    // Render the coupon as an image and let the player save it to their camera roll.
    try {
      const blob = await buildCouponImage();
      if (blob) {
        const file = new File([blob], `${coupon.code || `cupon-${P.key}`}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `Cupón ${P.name}` });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.name;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        }
      }
    } catch {}
    setSaved(true);
  };

  return (
    <div
      data-testid="coupon-modal"
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/75 backdrop-blur-sm p-3 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm my-auto max-h-[94vh] overflow-y-auto rounded-2xl overflow-x-hidden pixel-card p-0"
        style={{ background: "linear-gradient(160deg,#12294f 0%,#0a1c38 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="coupon-close-btn"
          onClick={onClose}
          className="absolute top-2.5 right-2.5 z-20 text-white/70 hover:text-white"
        >
          <X size={22} strokeWidth={2.5} />
        </button>

        <div className="pt-5 px-5 text-center">
          <div className="flex items-center justify-center gap-3" data-testid="coupon-logos">
            <img src={`${base}/nicabros-logo.webp`} alt="NicaBros" className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-xl bg-white/95 p-1.5 ring-2 ring-[#0067C6]/60" data-testid="coupon-logo-nicabros" />
            <span className="font-pixel text-lg text-white/60">×</span>
            <img src={`${base}/${P.logo}.webp`} alt={P.name} className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-xl bg-white/95 p-1.5 ring-2 ring-[#FFD54F]/70" data-testid="coupon-logo" />
          </div>
          <div className="mt-3 inline-flex items-center gap-1.5 font-pixel text-[9px] text-[#0a1c38] bg-[#FFD54F] px-2.5 py-1 rounded-full">
            <Ticket size={12} strokeWidth={2.5} /> {es ? "PREMIO ESPECIAL" : "SPECIAL REWARD"}
          </div>
          <h2 className="font-pixel text-2xl sm:text-3xl text-[#FFD54F] pixel-stroke mt-3 leading-none" data-testid="coupon-percent">
            {coupon.percent || 10}% OFF
          </h2>
          <p className="font-vt text-lg text-white/90 mt-1.5 leading-tight">
            {es
              ? `de toda tu cuenta en ${P.full}, ${P.city}`
              : `your entire bill at ${P.full}, ${P.city}`}
          </p>
        </div>

        {/* Ticket code */}
        <div className="mx-5 mt-4 rounded-xl bg-white/10 ring-1 ring-white/20 p-3 text-center">
          <p className="font-pixel text-[8px] text-white/60">{es ? "TU CÓDIGO" : "YOUR CODE"}</p>
          <p className="font-pixel text-base sm:text-lg text-white tracking-wider truncate" data-testid="coupon-code">{coupon.code}</p>
        </div>

        {expLabel && (
          <p className="mt-3 text-center font-vt text-base text-[#FFD54F] flex items-center justify-center gap-1.5" data-testid="coupon-expiry">
            <Clock size={14} strokeWidth={2.5} /> {es ? "Vence el" : "Expires"} {expLabel}
          </p>
        )}

        <div className="p-5 pt-3">
          {/* Gift Card Challenge hook */}
          <div className="mb-3 rounded-xl bg-[#FFD54F]/10 ring-1 ring-[#FFD54F]/40 px-3 py-2.5 text-center" data-testid="coupon-board-hook">
            <p className="font-pixel text-[9px] text-[#FFD54F] flex items-center justify-center gap-1.5"><Trophy size={12} strokeWidth={2.5} /> {t("etBoardTitle")}</p>
            {challenge ? (
              user ? (
                <>
                <p className="font-vt text-base text-[#8BC34A] mt-1 flex items-center justify-center gap-1.5" data-testid="coupon-board-saved">
                  <Check size={14} strokeWidth={3} /> {t("etScoreSaved")}{entry ? ` · ${entry.rank ? t("gcEntryLine")(entry.rank, entry.points.toLocaleString()) : `${entry.points.toLocaleString()} pts`}` : ""}
                </p>
                {entry && run && (
                  <ShareRunButton className="mt-2 w-full" onShare={() => shareRunCard({ challenge, run: { points: entry.points, score: run.score || 0, beers: run.beers || 0, deaths: Math.max(0, (run.maxLives || 0) - (run.lives || 0)), time_ms: run.timeMs || 0 }, username: user.username, rank: entry.rank, es })} />
                )}
                </>
              ) : (
                <>
                  <p className="font-vt text-base text-white/85 mt-1 leading-tight" data-testid="coupon-board-pending">{t("etScorePending")((entry?.points ?? score).toLocaleString())}</p>
                  {onAuth && (
                    <button data-testid="coupon-board-register-btn" onClick={() => onAuth("register")} className="mt-2 w-full pixel-btn bg-[#FFD54F] text-[#0a1c38] px-3 py-2 text-[10px] flex items-center justify-center gap-1.5">
                      <UserPlus size={13} strokeWidth={2.5} /> {t("etBoardRegister")}
                    </button>
                  )}
                </>
              )
            ) : (
              <>
                <p className="font-vt text-base text-white/85 mt-1 leading-tight" data-testid="coupon-board-try">{t("gcTryChallenge")}</p>
                {onChallenge && (
                  <button data-testid="coupon-challenge-btn" onClick={onChallenge} className="mt-2 w-full pixel-btn bg-[#E91E63] text-white px-3 py-2 text-[10px] flex items-center justify-center gap-1.5">
                    <Zap size={13} strokeWidth={2.5} /> {t("gcPlay")}
                  </button>
                )}
              </>
            )}
          </div>
          <button
            data-testid="coupon-save-btn"
            onClick={saveToPhone}
            disabled={saved}
            className="w-full pixel-btn bg-[#4CAF50] text-white px-4 py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-80"
          >
            {saved ? <Check size={18} strokeWidth={3} /> : <Smartphone size={18} strokeWidth={2.5} />}
            {saved ? (es ? "¡Guardado en tu galería!" : "Saved to your gallery!") : (es ? "Guardar en el carrete" : "Save to camera roll")}
          </button>
          <p className="mt-2.5 text-center font-vt text-sm text-white/55 leading-tight">
            {es
              ? `Mostrá este cupón en ${P.name} para reclamar tu descuento. Consejo: tomale una captura de pantalla.`
              : `Show this coupon at ${P.name} to claim your discount. Tip: take a screenshot too.`}
          </p>
          <button
            data-testid="coupon-share-btn"
            onClick={() => {
              const url = P.shareUrl;
              const pct = coupon.percent || 10;
              const text = es
                ? `¡Jugá el nivel de ${P.name} en Nica Bros y ganá ${pct}% de descuento en tu próxima cuenta! 🇳🇮${P.emoji}`
                : `Play the ${P.name} level in Nica Bros and get ${pct}% off your next bill! 🇳🇮${P.emoji}`;
              shareNicaBros({ text, url });
            }}
            className="mt-3 w-full pixel-btn bg-[#0067C6] text-white px-4 py-3 text-sm flex items-center justify-center gap-2"
          >
            <Share2 size={17} strokeWidth={2.5} /> {es ? "Compartir esta oferta" : "Share this offer"}
          </button>

          {/* Game reminders — capture an email so we can send specials/news */}
          <div className="mt-4 pt-4 border-t border-white/10" data-testid="coupon-reminders">
            {!leadDone ? (
              <>
                <p className="font-vt text-sm text-white/75 leading-tight flex items-center justify-center gap-1.5 text-center">
                  <Bell size={14} strokeWidth={2.5} className="text-[#FFD54F]" />
                  {es ? "Recibí ofertas y recordatorios del juego" : "Get game offers & reminders"}
                </p>
                <div className="mt-2 flex gap-2">
                  <input
                    data-testid="coupon-email-input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") captureEmail(); }}
                    placeholder={es ? "tucorreo@ejemplo.com" : "you@example.com"}
                    className="flex-1 min-w-0 bg-black/30 border-2 border-white/20 rounded-md px-3 py-2.5 font-vt text-base text-white placeholder-white/40 focus:outline-none focus:border-[#FFD54F]"
                  />
                  <button
                    data-testid="coupon-email-save-btn"
                    onClick={captureEmail}
                    disabled={leadSaving}
                    className="shrink-0 pixel-btn bg-[#FFD54F] text-[#0a1c38] px-4 py-2.5 text-[11px] whitespace-nowrap disabled:opacity-70"
                  >
                    {leadSaving ? (es ? "…" : "…") : (es ? "Avisarme" : "Notify me")}
                  </button>
                </div>
              </>
            ) : (
              <p className="font-vt text-base text-[#4CAF50] text-center flex items-center justify-center gap-1.5" data-testid="coupon-email-thanks">
                <Check size={15} strokeWidth={3} /> {es ? "¡Listo! Te mantendremos al día." : "Done! We'll keep you posted."}
              </p>
            )}
          </div>

          {(onReplay || onPlayManagua) && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {onReplay && (
                <button
                  data-testid="coupon-replay-btn"
                  onClick={onReplay}
                  className="pixel-btn bg-white/10 text-white px-3 py-2.5 text-[10px] flex items-center justify-center gap-1.5 ring-1 ring-white/20"
                >
                  <RotateCcw size={14} strokeWidth={2.5} /> {es ? "Jugar de nuevo" : "Play again"}
                </button>
              )}
              {onPlayManagua && (
                <button
                  data-testid="coupon-play-managua-btn"
                  onClick={onPlayManagua}
                  className="pixel-btn bg-[#4CAF50] text-white px-3 py-2.5 text-[10px] flex items-center justify-center gap-1.5"
                >
                  <Play size={14} strokeWidth={2.5} /> {es ? "Jugar Managua" : "Play Managua"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CouponModal;
